import {
  FlatPhoneReport,
  FlatReportAddressHistory,
  FlatReportEmailPhoneOrSocialInfo,
  FlatReportEmails,
  FlatReportOwners,
  FlatReportPhoneNumbers,
} from 'components/ChatBV/types/DeepDiveReportType';

import { normalizeArray, normalizeValue } from '../generalUtils';

export const mapPhoneReportToFlatSections = (
  reportData: any
): FlatPhoneReport => {
  const person = reportData?.entities?.people?.[0];
  const supportingPhone = reportData?.supporting?.phones?.[0];
  const searchData = reportData?.meta?.search_data;
  return {
    permalink: normalizeValue(reportData?.meta?.permalink),
    // IDENTITY: Names, aliases, ages, DOBs
    identity_or_owner_identity_info: {
      full_name: normalizeValue(person?.identity?.names?.[0]?.full),
      first_name: normalizeValue(
        person?.identity?.names?.[0]?.parsed?.first || searchData?.first_name
      ),
      middle_name: normalizeValue(
        person?.identity?.names?.[0]?.parsed?.middle || searchData?.middle_name
      ),
      last_name: normalizeValue(
        person?.identity?.names?.[0]?.parsed?.last || searchData?.last_name
      ),
      bvids: normalizeArray(searchData?.bvids || []),
      ids: normalizeArray(person?.identity?.ids || []),
      ddids: normalizeArray(person?.identity?.ddids || []),
      all_names: normalizeArray(
        person?.identity?.names?.map((n: any) => n.full) || []
      ),
      ages: normalizeArray(person?.identity?.ages || []),
      dobs: normalizeArray(person?.identity?.dobs || []),
      dods: normalizeArray(person?.identity?.dods || []),
      names: normalizeArray(
        supportingPhone?.associated?.names?.map((n: any) => n.full) || []
      ),

      owners: [
        {
          name: normalizeValue(person?.identity?.names?.[0]?.full),
          age: normalizeValue(person?.identity?.ages?.[0]),
          address: normalizeValue(person?.contact?.addresses?.[0]?.full),
          phone: normalizeValue(person?.contact?.phones?.[0]?.number),
          email: normalizeValue(person?.contact?.emails?.[0]?.address),
          social: normalizeValue(person?.social?.usernames?.[0]?.username),
          job: normalizeValue(person?.jobs?.[0]?.company),
          education: normalizeValue(person?.education?.[0]?.degree),
          criminal: normalizeValue(person?.criminal_events?.events?.[0]?.event),
        },
      ] as FlatReportOwners[],

      // DEMOGRAPHIC: Gender, ethnicity, languages
      demographic: {
        gender: normalizeValue(person?.demographic?.gender),
        ethnicities: normalizeArray(person?.demographic?.ethnicities || []),
        origin_countries: normalizeArray(
          person?.demographic?.origin_countries || []
        ),
        languages: normalizeArray(person?.demographic?.languages || []),
      },

      financial: {
        business_filings: normalizeArray(person?.filings?.business || []),
        ucc_filings: normalizeArray(person?.filings?.ucc || []),
      },

      obituary: {
        id: normalizeValue(person?.obituary?.id),
        funeral_home: normalizeValue(person?.obituary?.funeral_home),
        funeral_home_url: normalizeValue(person?.obituary?.funeral_home_url),
        funeral_home_address: normalizeValue(
          person?.obituary?.funeral_home_address
        ),
        message: normalizeValue(person?.obituary?.message),
        person_url: normalizeValue(person?.obituary?.person_url),
        photo_url: normalizeValue(person?.obituary?.photo_url),
        schedule: normalizeValue(person?.obituary?.schedule),
        recent_city: normalizeValue(person?.obituary?.recent_city),
        recent_region: normalizeValue(person?.obituary?.recent_region),
        zip5: normalizeValue(person?.obituary?.zip5),
        date_created: normalizeValue(person?.obituary?.date_created),
        last_updated: normalizeValue(person?.obituary?.last_updated),
        grave: normalizeValue(person?.grave),
      },
    },

    family_info: {
      // MARITAL: Marriage and divorce records
      marital: {
        marriages: normalizeArray(person?.marital?.marriages || []),
        divorces: normalizeArray(person?.marital?.divorces || []),
      },

      // FAMILY: Relatives, genealogy, connections
      family: {
        genealogy: normalizeValue(person?.genealogy),
        connections: normalizeValue(person?.connections),
      },
    },

    // Phone numbers, emails, addresses
    email_phone_or_social_info: {
      phone_number: normalizeValue(
        supportingPhone?.number || searchData?.phone_number
      ),
      primary_address: normalizeValue(person?.contact?.addresses?.[0]?.full),
      city: normalizeValue(person?.contact?.addresses?.[0]?.parsed?.city),
      state: normalizeValue(person?.contact?.addresses?.[0]?.parsed?.state),
      zip5: normalizeValue(person?.contact?.addresses?.[0]?.parsed?.zip5),
      zip4: normalizeValue(person?.contact?.addresses?.[0]?.parsed?.zip4),
      county: normalizeValue(person?.contact?.addresses?.[0]?.county?.name),
      county_fips: normalizeValue(
        person?.contact?.addresses?.[0]?.county?.fips
      ),
      congressional_district: normalizeValue(
        person?.contact?.addresses?.[0]?.congressional_district
      ),
      phones: normalizeArray(
        person?.contact?.phones?.slice(0, 3)?.map((phone: any) => ({
          phone: normalizeValue(phone?.number),
          type: normalizeValue(phone?.type),
        })) || []
      ) as FlatReportPhoneNumbers[] | undefined,
      emails: normalizeArray(
        person?.contact?.emails?.slice(0, 3)?.map((email: any) => ({
          address: normalizeValue(email?.address),
          type: normalizeValue(email?.type),
        })) || []
      ) as FlatReportEmails[] | undefined,
      addresses: normalizeArray(
        person?.contact?.addresses?.slice(0, 3)?.map((address: any) => ({
          address: normalizeValue(address?.full),
          type: normalizeValue(address?.type),
        })) || []
      ) as FlatReportAddressHistory[] | undefined,
      // CONNECTIVITY: Carrier, line type, phone activity
      phone_connectivity_info: {
        is_active:
          normalizeValue(supportingPhone?.connectivity?.is_active) ?? false,
        is_valid: normalizeValue(supportingPhone?.is_valid) ?? false,
        active_status: normalizeValue(
          supportingPhone?.connectivity?.active_status
        ),
        carrier: normalizeValue(
          supportingPhone?.connectivity?.carrier || searchData?.carrier
        ),
        line_type: normalizeValue(supportingPhone?.connectivity?.line_type),
        user_activity: normalizeValue(
          supportingPhone?.connectivity?.user_activity
        ),
        mcc: normalizeValue(supportingPhone?.connectivity?.mcc),
        mnc: normalizeValue(supportingPhone?.connectivity?.mnc),
        dialing_code: normalizeValue(supportingPhone?.dialing_code) ?? 1,
      },
      // SOCIAL: Social media profiles, usernames
      social: {
        profiles: normalizeArray(person?.social?.profiles || []),
        usernames: normalizeArray(person?.social?.usernames || []),
        user_ids: normalizeArray(person?.social?.user_ids || []),
      },
    } as FlatReportEmailPhoneOrSocialInfo,

    trust_and_safety_status_info: {
      is_risky: normalizeValue(supportingPhone?.risk?.is_risky) ?? false,
      is_spammer: normalizeValue(supportingPhone?.risk?.is_spammer) ?? false,
      is_recent_abuse:
        normalizeValue(supportingPhone?.risk?.is_recent_abuse) ?? false,
      is_leaked: normalizeValue(supportingPhone?.risk?.is_leaked) ?? false,
      is_do_not_call:
        normalizeValue(supportingPhone?.risk?.is_do_not_call) ?? false,
      is_voip: normalizeValue(supportingPhone?.risk?.is_voip) ?? false,
      is_prepaid: normalizeValue(supportingPhone?.risk?.is_prepaid),
      fraud_score: normalizeValue(supportingPhone?.risk?.fraud_score),
      spam_score: normalizeValue(supportingPhone?.spam_score) ?? 0,
      spam_reports_count:
        normalizeValue(supportingPhone?.spam_reports_count) ?? 0,
    },

    property_or_address_info: {
      city: normalizeValue(
        supportingPhone?.location?.address?.parsed?.city || searchData?.city
      ),
      state: normalizeValue(
        supportingPhone?.location?.address?.parsed?.state || searchData?.state
      ),
      zip5: normalizeValue(supportingPhone?.location?.address?.parsed?.zip5),
      country: normalizeValue(
        supportingPhone?.location?.address?.parsed?.country
      ),
      timezone: normalizeValue(supportingPhone?.location?.timezone),
      latitude: normalizeValue(person?.contact?.addresses?.[0]?.geo?.latitude),
      longitude: normalizeValue(
        person?.contact?.addresses?.[0]?.geo?.longitude
      ),
      precision: normalizeValue(
        person?.contact?.addresses?.[0]?.geo?.precision
      ),
      observe_dst:
        normalizeValue(person?.contact?.addresses?.[0]?.observe_dst) ?? false,
    },

    // CRIMINAL: Criminal records and events
    criminal_or_legal_info: {
      has_records: (person?.criminal_events?.events?.length || 0) > 0,
      events: normalizeArray(person?.criminal_events?.events || []),
      court: {
        bankruptcy: normalizeArray(person?.courts?.bankruptcy || []),
        civil: normalizeArray(person?.courts?.civil || []),
        criminal: normalizeArray(person?.courts?.criminal || []),
      },
    },

    // VEHICLES: Automobiles, aircraft, watercraft
    vehicles_info: {
      owned_assets: {
        aircraft: normalizeArray(person?.vehicles?.aircraft || []),
        automobiles: normalizeArray(person?.vehicles?.automobiles || []),
        watercraft: normalizeArray(person?.vehicles?.watercraft || []),
      },
    },

    // EMPLOYMENT: Jobs, education, skills
    jobs_education_or_skills_info: {
      jobs: normalizeArray(person?.jobs || []),
      educations: normalizeArray(person?.educations || []),
      skills: normalizeArray(person?.skills || []),
    },
  } as FlatPhoneReport;
};
